import { useCallback, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { api, type Reader } from '@/api/client';
import { useCellCommentShellOptional } from '@/features/project-cell-comments/cellCommentShellContext';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { Button } from '@/components/ui/Button/Button';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import { EditIcon, DeleteIcon } from '@/components/ui/Icons';
import { Textarea } from '@/components/ui/Textarea/Textarea';
import { Badge } from '@/components/ui/Badge/Badge';
import { useToast } from '@/contexts/ToastContext';
import { useCatalogManageOpener, useCanManageCatalog } from '@/contexts/CatalogManageOpenerContext';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import {
  MAX_NAME_NOTE_LEN,
  NAME_NOTE_TEXTAREA_MAX_ROWS,
  nameNoteSingleLine,
} from '@/utils/nameNote';
import { readerCatalogTableDisplayName } from '@/utils/readerCatalogTableDisplayName';
import {
  cancelReadersTableListQueries,
  optimisticPatchReaderInTableCaches,
  patchReaderInTableCache,
  invalidateReaderCachesExceptTable,
  restoreReadersTableInfiniteQueries,
  snapshotReadersTableInfiniteQueries,
  type ReadersTableInfiniteSnapshot,
} from '@/lib/readersCatalogQueryCache';
import '@/components/ProjectsTable/EditableCell.css';
import { EditableCellPopoverHeader } from '@/components/ProjectsTable/EditableCellPopoverHeader';
import { highlightPlainText, SEARCH_HIGHLIGHT_CLASS_READERS_CATALOG } from '@/utils/highlightSearchTerms';

type ReadersTableMutationCtx = { snapshot: ReadersTableInfiniteSnapshot };

export interface ReaderCatalogNameCellProps {
  reader: Reader;
  disabled?: boolean;
  /** Слова из поиска каталога (подсветка ФИО и заметки к имени). */
  searchHighlightWords?: string[];
}

export function ReaderCatalogNameCell({
  reader,
  disabled = false,
  searchHighlightWords = [],
}: ReaderCatalogNameCellProps) {
  const cellCommentShell = useCellCommentShellOptional();
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const openCatalogManage = useCatalogManageOpener();
  const canManageCatalog = useCanManageCatalog();

  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [nameNotePopoverOpen, setNameNotePopoverOpen] = useState(false);
  const [nameNoteDraft, setNameNoteDraft] = useState('');
  const [nameNoteError, setNameNoteError] = useState('');

  const cellRef = useRef<HTMLDivElement>(null);
  const nameNotePopoverRef = useRef<HTMLDivElement>(null);

  const {
    shouldRender: nameNoteShouldRender,
    contentClasses: nameNoteContentClasses,
    requestClose: requestNameNoteClose,
    isExiting: nameNoteIsExiting,
  } = useOverlayAnimation(
    nameNotePopoverOpen,
    useCallback(() => {
      setNameNotePopoverOpen(false);
      setNameNoteError('');
    }, []),
    true
  );

  usePopoverPositioning({
    triggerRef: cellRef as React.RefObject<HTMLElement | null>,
    popoverRef: nameNotePopoverRef,
    isOpen: nameNoteShouldRender,
    preferredPositionY: 'bottom',
    preferredPositionX: 'left',
    offset: 8,
    zIndex: 1001,
    dependencies: [nameNotePopoverOpen],
  });

  useClickOutside(nameNotePopoverRef, requestNameNoteClose as (e: MouseEvent) => void, {
    excludeRefs: [cellRef],
    excludeSelector: '[data-combo-dropdown]',
    enabled: nameNoteShouldRender,
    capture: false,
  });

  const updateMutation = useMutation<
    Reader,
    Error,
    { name_note: string | null },
    ReadersTableMutationCtx
  >({
    mutationFn: (data) => api.readers.update(reader.id, data),
    onMutate: async (vars) => {
      await cancelReadersTableListQueries(queryClient);
      const snapshot = snapshotReadersTableInfiniteQueries(queryClient);
      optimisticPatchReaderInTableCaches(queryClient, reader.id, { name_note: vars.name_note });
      return { snapshot };
    },
    onSuccess: async (updated) => {
      showSuccess('Данные сохранены');
      patchReaderInTableCache(queryClient, updated);
      await invalidateReaderCachesExceptTable(queryClient);
      setNameNotePopoverOpen(false);
      setNameNoteError('');
    },
    onError: (err: Error, _vars, ctx) => {
      if (ctx?.snapshot) {
        restoreReadersTableInfiniteQueries(queryClient, ctx.snapshot);
      }
      const msg = err instanceof Error ? err.message : String(err);
      showError(msg);
      setNameNoteError(msg);
    },
  });

  const isPending = updateMutation.isPending;

  const displayName = readerCatalogTableDisplayName(reader);
  const nameNoteText = nameNoteSingleLine(reader.name_note);
  const highlightedDisplayName = useMemo(() => {
    if (searchHighlightWords.length === 0) return displayName;
    return highlightPlainText(displayName, searchHighlightWords, SEARCH_HIGHLIGHT_CLASS_READERS_CATALOG);
  }, [displayName, searchHighlightWords]);

  const highlightedNameNote = useMemo(() => {
    if (!nameNoteText || searchHighlightWords.length === 0) return nameNoteText;
    return highlightPlainText(nameNoteText, searchHighlightWords, SEARCH_HIGHLIGHT_CLASS_READERS_CATALOG);
  }, [nameNoteText, searchHighlightWords]);

  const openNameNotePopover = useCallback(() => {
    setNameNoteDraft(nameNoteSingleLine(reader.name_note));
    setNameNoteError('');
    setNameNotePopoverOpen(true);
  }, [reader.name_note]);

  const handleSaveNameNote = useCallback(() => {
    setNameNoteError('');
    const trimmed = nameNoteSingleLine(nameNoteDraft);
    if (!trimmed) {
      setNameNoteError('Введите текст заметки');
      return;
    }
    if (trimmed.length > MAX_NAME_NOTE_LEN) {
      setNameNoteError(`Не более ${MAX_NAME_NOTE_LEN} символов`);
      return;
    }
    updateMutation.mutate({ name_note: trimmed });
  }, [nameNoteDraft, updateMutation]);

  const handleRemoveNameNoteConfirmed = useCallback(() => {
    if (!window.confirm('Удалить заметку к имени чтеца?')) return;
    updateMutation.mutate({ name_note: null });
  }, [updateMutation]);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      setContextMenuPos({ x: e.clientX, y: e.clientY });
      setContextMenuOpen(true);
    },
    [disabled]
  );

  const handleEditIdentityFromMenu = useCallback(() => {
    setContextMenuOpen(false);
    openCatalogManage({ tab: 'persons', personId: reader.id });
  }, [openCatalogManage, reader.id]);

  const contextMenuContent = useMemo(() => {
    const commentItem = cellCommentShell?.addCommentMenuItem;
    const hasNote = !!nameNoteSingleLine(reader.name_note);
    return (
      <ul className="context-menu__list" role="menu">
        {canManageCatalog ? (
          <li className="context-menu__list-item">
            <button
              type="button"
              className="context-menu__item"
              disabled={!!disabled}
              role="menuitem"
              onClick={handleEditIdentityFromMenu}
            >
              <span className="context-menu__iconCell" aria-hidden>
                <EditIcon size={18} />
              </span>
              <span className="context-menu__item-text">Редактировать</span>
            </button>
          </li>
        ) : null}
        {commentItem ? (
          <li className="context-menu__list-item">
            <button
              type="button"
              className="context-menu__item"
              disabled={!!disabled}
              role="menuitem"
              onClick={() => {
                setContextMenuOpen(false);
                commentItem.onClick();
              }}
            >
              <span className="context-menu__iconCell" aria-hidden>
                {commentItem.icon}
              </span>
              <span className="context-menu__item-text">{commentItem.label}</span>
            </button>
          </li>
        ) : null}
        {hasNote ? (
          <>
            <li className="context-menu__list-item">
              <button
                type="button"
                className="context-menu__item"
                disabled={!!disabled}
                role="menuitem"
                onClick={() => {
                  setContextMenuOpen(false);
                  openNameNotePopover();
                }}
              >
                <span className="context-menu__iconCell" aria-hidden>
                  <i className="fa-solid fa-file-pen editable-cell__context-menu-fa" />
                </span>
                <span className="context-menu__item-text">Изменить заметку</span>
              </button>
            </li>
            <li className="context-menu__list-item">
              <button
                type="button"
                className="context-menu__item context-menu__item_danger"
                disabled={!!disabled}
                role="menuitem"
                onClick={() => {
                  setContextMenuOpen(false);
                  handleRemoveNameNoteConfirmed();
                }}
              >
                <span className="context-menu__iconCell" aria-hidden>
                  <DeleteIcon size={18} />
                </span>
                <span className="context-menu__item-text">Удалить заметку</span>
              </button>
            </li>
          </>
        ) : (
          <li className="context-menu__list-item">
            <button
              type="button"
              className="context-menu__item"
              disabled={!!disabled}
              role="menuitem"
              onClick={() => {
                setContextMenuOpen(false);
                openNameNotePopover();
              }}
            >
              <span className="context-menu__iconCell" aria-hidden>
                <i className="fa-solid fa-file editable-cell__context-menu-fa" />
              </span>
              <span className="context-menu__item-text">Добавить заметку</span>
            </button>
          </li>
        )}
      </ul>
    );
  }, [
    cellCommentShell,
    canManageCatalog,
    disabled,
    handleEditIdentityFromMenu,
    handleRemoveNameNoteConfirmed,
    openNameNotePopover,
    reader,
  ]);

  const hasAnchor = !!cellRef.current;
  const nameNotePopoverAnchorStyle: CSSProperties = {
    position: 'fixed',
    pointerEvents: nameNoteIsExiting ? 'none' : 'auto',
    left: hasAnchor ? -9999 : 8,
    top: hasAnchor ? -9999 : 8,
  };
  const nameNotePopoverPanelStyle: CSSProperties = {
    width: 320,
    minWidth: 280,
    maxWidth: 'calc(100vw - 16px)',
    maxHeight: '70vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: 'var(--overlay-bg)',
    backdropFilter: 'var(--overlay-blur)',
    WebkitBackdropFilter: 'var(--overlay-blur)',
    border: 'var(--overlay-border)',
    borderRadius: 'var(--overlay-radius)',
    boxShadow: 'var(--overlay-shadow)',
  };

  return (
    <>
      <div
        ref={cellRef}
        className={clsx('editable-cell', disabled && 'readers-table-catalog__editable-cell_disabled')}
        onContextMenu={handleContextMenu}
      >
        <div className="editable-cell__content">
          <div className="editable-cell__name-stack readers-table-catalog__name-stack">
            <div className="readers-table-catalog__name-block readers-table-catalog__name-block_in-stack">
              <strong>{highlightedDisplayName}</strong>
            </div>
            {nameNoteText ? (
              <div className="editable-cell__name-note-wrap">
                <div
                  className="editable-cell__name-note-hit"
                  role="button"
                  tabIndex={disabled ? -1 : 0}
                  onClick={() => {
                    if (disabled) return;
                    openNameNotePopover();
                  }}
                  onKeyDown={(e) => {
                    if (disabled) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openNameNotePopover();
                    }
                  }}
                  aria-label="Редактировать заметку к имени"
                >
                  <Badge variant="custom" bg="var(--gradient-error)" color="#fff">
                    <span className="editable-cell__name-note-label">{highlightedNameNote}</span>
                  </Badge>
                </div>
              </div>
            ) : null}
          </div>
        </div>
        <ContextMenu isOpen={contextMenuOpen} onClose={() => setContextMenuOpen(false)} anchorPosition={contextMenuPos}>
          {contextMenuContent}
        </ContextMenu>
      </div>
      {nameNoteShouldRender &&
        createPortal(
          <div ref={nameNotePopoverRef} style={nameNotePopoverAnchorStyle}>
            <OverlayIntrinsicSize>
              <div
                style={nameNotePopoverPanelStyle}
                className={`editable-cell-popover editable-cell-popover_name-note ${nameNoteContentClasses}`}
              >
                <EditableCellPopoverHeader onClose={requestNameNoteClose}>Заметка к имени</EditableCellPopoverHeader>
                <div className="editable-cell-popover__form">
                  <div className="editable-cell-popover__form-stack">
                    <Textarea
                      value={nameNoteDraft}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\r\n|\r|\n/g, ' ').slice(0, MAX_NAME_NOTE_LEN);
                        setNameNoteDraft(v);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.preventDefault();
                      }}
                      placeholder="До 40 символов"
                      fullWidth
                      autoFocus
                      minRows={1}
                      maxRows={NAME_NOTE_TEXTAREA_MAX_ROWS}
                      error={nameNoteError}
                      characterCount={{ current: nameNoteDraft.length, max: MAX_NAME_NOTE_LEN }}
                      disabled={isPending}
                    />
                  </div>
                </div>
                <div className="editable-cell-popover__footer">
                  {nameNoteSingleLine(reader.name_note) ? (
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={handleRemoveNameNoteConfirmed}
                      disabled={isPending}
                    >
                      Удалить
                    </Button>
                  ) : null}
                  <Button type="button" variant="primary" onClick={handleSaveNameNote} disabled={isPending}>
                    {updateMutation.isPending ? 'Сохранение…' : 'Сохранить'}
                  </Button>
                </div>
              </div>
            </OverlayIntrinsicSize>
          </div>,
          document.body
        )}
    </>
  );
}
